/**
 * The Summit title's snowstorm, as a small particle field.
 *
 * It replaced a pair of tiled CSS layers, which read as lines rather than snow:
 * a repeating tile lays its flakes out in rows, and a whole layer sliding at one
 * speed turns each row into a streak. Here every flake has its own position,
 * size, fall speed and sway, and none of them is tied to another.
 *
 * The wind is the part that makes it weather. A steady breeze drifts the snow
 * sideways at roughly the speed the old layers ran at, with a slow wander on
 * top; every few seconds a **gust** ramps in, holds and dies away. Flakes do not
 * jump to the wind's speed — each one eases towards it, nearer (bigger) flakes
 * faster than far ones — so a gust visibly sweeps through the field and the
 * snow settles back to drifting after it.
 *
 * Everything is measured in **em** of the badge's font, so the plate over a tank
 * and the shop card show the same storm at their own sizes. Pure apart from the
 * random source it is handed, so it is tested without a canvas or a clock.
 */

export interface Flake {
  x: number;
  y: number;
  /** Radius, em. */
  r: number;
  /** 0.4 (far) – 1 (near). Scales speed, size, brightness and wind response. */
  depth: number;
  /** Horizontal velocity, em/s — eased towards the wind, never set to it. */
  vx: number;
  /** Fall speed, em/s. */
  fall: number;
  /** Sway phase and rate, so no two flakes wobble together. */
  phase: number;
  swayRate: number;
}

export interface Gust {
  /** Seconds since the field started. */
  start: number;
  attack: number;
  hold: number;
  release: number;
  /** Extra wind at the peak, em/s. */
  strength: number;
}

export interface SnowField {
  /** Size of the area, em. */
  w: number;
  h: number;
  t: number;
  flakes: Flake[];
  gust: Gust | null;
  nextGustAt: number;
  rand: () => number;
}

/** The steady breeze, em/s. A storm's, not a flurry's. */
export const SNOW_BASE_WIND = 2.6;
/** How far the breeze wanders either side of its base, as a fraction. */
export const SNOW_WIND_WANDER = 0.25;
/** Seconds between the end of one gust and the start of the next. */
export const SNOW_GUST_GAP: readonly [number, number] = [2.5, 6.5];
/** Extra wind a gust adds at its peak, em/s. */
export const SNOW_GUST_STRENGTH: readonly [number, number] = [3.4, 6];
/** How quickly a near flake catches up with the wind (1/s). Far flakes are slower. */
export const SNOW_DRAG = 2.4;
/** Flakes per square em of badge, and a cap for a very long one. */
export const SNOW_DENSITY = 0.8;
export const SNOW_MAX_FLAKES = 70;

const between = (rand: () => number, [lo, hi]: readonly [number, number]): number =>
  lo + rand() * (hi - lo);

/** A seeded generator, so a field can be replayed in a test. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function newFlake(rand: () => number, w: number, h: number): Flake {
  const depth = 0.4 + rand() * 0.6;
  return {
    x: rand() * w,
    y: rand() * h,
    r: 0.035 + depth * 0.075,
    depth,
    vx: SNOW_BASE_WIND * depth,
    fall: 0.9 + depth * 1.2 + rand() * 0.4,
    phase: rand() * Math.PI * 2,
    swayRate: 1.2 + rand() * 1.8,
  };
}

export function createSnowField(w: number, h: number, rand: () => number = Math.random): SnowField {
  const count = Math.max(6, Math.min(SNOW_MAX_FLAKES, Math.round(w * h * SNOW_DENSITY)));
  return {
    w,
    h,
    t: 0,
    flakes: Array.from({ length: count }, () => newFlake(rand, w, h)),
    gust: null,
    nextGustAt: between(rand, [0.8, 3]),
    rand,
  };
}

/** A gust's contribution at time `t`: a smooth rise, a hold, a smooth fall. */
export function gustAt(gust: Gust | null, t: number): number {
  if (!gust) return 0;
  const s = t - gust.start;
  if (s <= 0) return 0;
  const ease = (x: number) => x * x * (3 - 2 * x);
  if (s < gust.attack) return gust.strength * ease(s / gust.attack);
  if (s < gust.attack + gust.hold) return gust.strength;
  const fall = s - gust.attack - gust.hold;
  if (fall < gust.release) return gust.strength * (1 - ease(fall / gust.release));
  return 0;
}

/** The wind across the whole field at its current time, em/s. */
export function windAt(field: SnowField): number {
  const t = field.t;
  const wander = Math.sin(t * 0.37) * 0.6 + Math.sin(t * 0.91 + 1.3) * 0.4;
  return SNOW_BASE_WIND * (1 + SNOW_WIND_WANDER * wander) + gustAt(field.gust, t);
}

/** Advances the field by `dt` seconds. A long stall is clamped, never replayed. */
export function stepSnow(field: SnowField, dt: number): void {
  const step = Math.min(Math.max(dt, 0), 0.1);
  field.t += step;
  const t = field.t;

  const g = field.gust;
  if (g && t > g.start + g.attack + g.hold + g.release) {
    field.gust = null;
    field.nextGustAt = t + between(field.rand, SNOW_GUST_GAP);
  }
  if (!field.gust && t >= field.nextGustAt) {
    field.gust = {
      start: t,
      attack: between(field.rand, [0.4, 0.9]),
      hold: between(field.rand, [0.3, 1.4]),
      release: between(field.rand, [1.2, 2.2]),
      strength: between(field.rand, SNOW_GUST_STRENGTH),
    };
  }

  const wind = windAt(field);
  const margin = 0.2;
  for (const f of field.flakes) {
    const target = wind * f.depth;
    f.vx += (target - f.vx) * Math.min(1, SNOW_DRAG * f.depth * step);
    const sway = Math.sin(f.phase + t * f.swayRate) * 0.25;
    f.x += (f.vx + sway) * step;
    // A gust lifts the snow a little as well as pushing it.
    f.y += (f.fall - gustAt(field.gust, t) * 0.08) * step;

    if (f.x > field.w + margin) {
      f.x -= field.w + margin * 2;
      f.y = field.rand() * field.h;
    } else if (f.x < -margin) {
      f.x += field.w + margin * 2;
    }
    if (f.y > field.h + margin) {
      f.y -= field.h + margin * 2;
      f.x = field.rand() * field.w;
    } else if (f.y < -margin) {
      f.y += field.h + margin * 2;
    }
  }
}

/** Draws the field. `em` is the badge's font size in canvas pixels. */
export function drawSnow(ctx: CanvasRenderingContext2D, field: SnowField, em: number): void {
  ctx.clearRect(0, 0, field.w * em, field.h * em);
  for (const f of field.flakes) {
    ctx.globalAlpha = 0.45 + f.depth * 0.5;
    ctx.beginPath();
    ctx.arc(f.x * em, f.y * em, Math.max(0.6, f.r * em), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}
